"use client";

import { useState, useEffect } from 'react';
import { parseBlob } from 'music-metadata';

interface AudioFile {
  id?: string;
  url?: string;
  secure_url?: string;
  public_id?: string;
  duration?: number;
  bytes?: number;
  format?: string;
  fileName?: string;
  metadata?: {
    title?: string;
    description?: string;
    artist?: string;
    album?: string;
    genre?: string;
    [key: string]: unknown;
  };
  context?: {
    caption?: string;
    alt?: string;
    [key: string]: unknown;
  };
  derived?: Array<{
    url?: string;
    secure_url?: string;
    raw_transformation?: string;
  }>;
}

interface AudioPlayerProps {
  audioFile: AudioFile;
  title?: string;
  showMetadata?: boolean;
}

export default function AudioPlayer({ audioFile, title, showMetadata = true }: AudioPlayerProps) {
  const [loadError, setLoadError] = useState<string | null>(null);
  const [extractedMetadata, setExtractedMetadata] = useState<{
    title?: string;
    artist?: string;
    album?: string;
    comment?: string;
  } | null>(null);

  // Extract metadata from audio file on component mount
  useEffect(() => {
    const audioUrl = audioFile.secure_url || audioFile.url;
    if (!audioUrl) return;

    const fetchMetadata = async () => {
      try {
        // Fetch the audio file as a blob
        const response = await fetch(audioUrl);
        const blob = await response.blob();

        // Parse metadata from the blob
        const metadata = await parseBlob(blob);

        // Extract comment - handle both string and array formats
        let commentText: string | undefined;
        if (metadata.common.comment) {
          if (Array.isArray(metadata.common.comment)) {
            const firstComment = metadata.common.comment[0];
            // Comment can be a string or an object with a text property
            if (typeof firstComment === 'string') {
              commentText = firstComment;
            } else if (firstComment && typeof firstComment === 'object' && 'text' in firstComment) {
              commentText = firstComment.text;
            }
          } else if (typeof metadata.common.comment === 'string') {
            commentText = metadata.common.comment;
          }
        }

        setExtractedMetadata({
          title: metadata.common.title,
          artist: metadata.common.artist,
          album: metadata.common.album,
          comment: commentText,
        });

        console.log('Extracted audio metadata:', metadata.common);
      } catch (error) {
        console.log('Could not read audio metadata:', error);
      }
    };

    fetchMetadata();
  }, [audioFile.secure_url, audioFile.url]);

  // Get audio URLs in order of preference
  const getAudioUrls = (): string[] => {
    const urls: string[] = [];
    
    // Add secure_url first (most reliable for CORS)
    if (audioFile.secure_url) {
      urls.push(audioFile.secure_url);
    }
    
    // Add regular url as fallback
    if (audioFile.url && audioFile.url !== audioFile.secure_url) {
      urls.push(audioFile.url);
    }
    
    // Add original quality versions if available
    const originalDerived = audioFile.derived?.find(d => 
      !d.raw_transformation || d.raw_transformation === '' || d.raw_transformation === 'q_auto/f_auto'
    );
    if (originalDerived?.secure_url && !urls.includes(originalDerived.secure_url)) {
      urls.unshift(originalDerived.secure_url);
    }
    
    return urls.filter(Boolean);
  };

  const audioUrls = getAudioUrls();

  const handleAudioError = () => {
    setLoadError('Failed to load audio file');
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  if (audioUrls.length === 0) {
    return (
      <div className="bg-black/20 rounded-lg p-4">
        <p className="text-red-400">No audio sources available</p>
      </div>
    );
  }

  // Extract title from extracted metadata, then fall back to filename
  const getAudioTitle = (): string | null => {
    // First priority: Title prop from Hygraph component
    if (title) {
      return title;
    }

    // Second priority: Cloudinary context metadata (caption/title)
    if (audioFile.context?.caption) {
      return audioFile.context.caption;
    }

    // Third priority: ID3 title tag from audio file
    if (extractedMetadata?.title) {
      return extractedMetadata.title;
    }

    // Fourth priority: Check if comment looks like readable text (not binary/hex data)
    if (extractedMetadata?.comment) {
      const isReadable = /^[\x20-\x7E\s]+$/.test(extractedMetadata.comment);
      if (isReadable && extractedMetadata.comment.length < 200) {
        return extractedMetadata.comment;
      }
    }

    // Fallback to filename or public_id
    if (audioFile.fileName) {
      return audioFile.fileName.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    }
    if (audioFile.public_id) {
      return audioFile.public_id.replace(/[-_]/g, ' ');
    }
    return null;
  };

  const audioTitle = getAudioTitle();

  return (
    <div className="bg-black/20 rounded-lg p-4">
      {audioTitle && (
        <h4 className="text-sm font-body mb-2">{audioTitle}</h4>
      )}

      {/* Show additional metadata from extracted audio tags */}
      {(extractedMetadata?.artist || extractedMetadata?.album) && (
        <div className="flex flex-wrap gap-3 mb-3 text-xs text-white/60">
          {extractedMetadata.artist && (
            <span>Artist: {extractedMetadata.artist}</span>
          )}
          {extractedMetadata.album && (
            <span>Album: {extractedMetadata.album}</span>
          )}
        </div>
      )}

      <audio
        controls
        className="w-full"
        preload="metadata"
        onError={handleAudioError}
      >
        {audioUrls.map((url, index) => {
          // Determine MIME type from URL or file extension
          const getMimeType = (url: string): string => {
            const lowerUrl = url.toLowerCase();
            if (lowerUrl.includes('.aiff') || lowerUrl.includes('.aif')) {
              return 'audio/aiff';
            }
            if (lowerUrl.includes('.wav')) {
              return 'audio/wav';
            }
            if (lowerUrl.includes('.mp3')) {
              return 'audio/mpeg';
            }
            if (lowerUrl.includes('.m4a') || lowerUrl.includes('.aac')) {
              return 'audio/mp4';
            }
            if (lowerUrl.includes('.ogg')) {
              return 'audio/ogg';
            }
            // Default fallback
            return 'audio/mpeg';
          };

          return (
            <source 
              key={index} 
              src={url} 
              type={getMimeType(url)}
            />
          );
        })}
        Your browser does not support the audio element.
      </audio>

      {loadError && (
        <div className="mt-2 p-2 bg-red-900/20 rounded border border-red-500/20">
          <p className="text-red-400 text-sm mb-2">{loadError}</p>
          <p className="text-sm text-white/70">
            You can try downloading the file directly:
          </p>
          {audioUrls.map((url, index) => (
            <a 
              key={index}
              href={url} 
              download
              className="text-blue-400 hover:text-blue-300 text-sm underline mr-4"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Option {index + 1}
            </a>
          ))}
        </div>
      )}

      {showMetadata && (
        <div className="flex flex-wrap gap-4 items-center mt-2 text-sm text-white/70">
          {audioFile.duration && (
            <span>Duration: {formatDuration(audioFile.duration)}</span>
          )}
          {audioFile.bytes && (
            <span>Size: {formatFileSize(audioFile.bytes)}</span>
          )}
          {audioFile.format && (
            <span>Format: {audioFile.format.toUpperCase()}</span>
          )}
        </div>
      )}

      {/* Only show filename if no title metadata was found */}
      {audioFile.fileName && !audioTitle && (
        <p className="text-xs text-white/50 mt-1">
          File: {audioFile.fileName}
        </p>
      )}

      {audioUrls.length > 1 && (
        <p className="text-xs text-white/50 mt-1">
          Multiple quality options available
        </p>
      )}
    </div>
  );
}